import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Layout,
  Sliders,
  Palette,
  Eye,
  Check,
  Plus,
  Trash2,
  ExternalLink,
  Info,
  Link2,
  Shield,
  Phone,
  Mail,
  MapPin,
  Building,
  Share2,
  Globe,
  Instagram,
  Youtube,
  Facebook,
  Linkedin,
  MessageCircle,
  Layers,
  Lock,
  ArrowUpRight,
  Rss,
  FileCode2,
} from 'lucide-react';
import { FooterConfig, FooterLinkItem, PageRoute, SocialLinksConfig } from '../../types.ts';
import { TikTokIcon, TwitterXIcon } from '../layout/Footer.tsx';

const BG_COLOR_PRESETS = [
  { name: 'Dark Slate (Default)', value: '#020617' },
  { name: 'Midnight Blue', value: '#0f172a' },
  { name: 'Deep Emerald', value: '#064e3b' },
  { name: 'Forest Green', value: '#14532d' },
  { name: 'Deep Indigo', value: '#1e1b4b' },
  { name: 'Charcoal Minimalist', value: '#18181b' },
  { name: 'Warm Dark Brown', value: '#271c19' },
  { name: 'Light Slate Theme', value: '#f8fafc' },
];

export default function FooterManager() {
  const { settings, updateSettingsOnServer, showToast, navigate, categories } = useApp();

  const [activeTab, setActiveTab] = useState<
    'all' | 'identity' | 'social' | 'nav' | 'seo' | 'contact' | 'bottom' | 'theme'
  >('all');

  const initialFooterConfig = settings?.footerConfig || {};
  const initialSocialConfig = initialFooterConfig.socialLinks || {};

  const [footerData, setFooterData] = useState<FooterConfig>({
    aboutTitle:
      initialFooterConfig.aboutTitle ||
      settings?.siteName ||
      'Greenjobs ITB',
    aboutText:
      initialFooterConfig.aboutText ||
      '',
    navTitle: initialFooterConfig.navTitle || 'Navigation',
    categoriesTitle: initialFooterConfig.categoriesTitle || 'Topics & Sectors',
    contactTitle: initialFooterConfig.contactTitle || 'Headquarters & Contact',
    copyrightText:
      initialFooterConfig.copyrightText ||
      `${settings?.siteName || 'Greenjobs ITB'}. All rights reserved.`,
    bgColor: initialFooterConfig.bgColor || initialFooterConfig.backgroundColor || '#020617',
    textColor: initialFooterConfig.textColor || '#94a3b8',
    showSocial: initialFooterConfig.showSocial ?? initialFooterConfig.showSocialLinks ?? true,
    showQuickLinks: initialFooterConfig.showQuickLinks ?? true,
    showCategories: initialFooterConfig.showCategories ?? true,
    showContact: initialFooterConfig.showContact ?? initialFooterConfig.showContactInfo ?? true,
    showSeoLinks: initialFooterConfig.showSeoLinks ?? true,
    showAdminLink: initialFooterConfig.showAdminLink ?? true,
    showWaChatBtn: initialFooterConfig.showWaChatBtn ?? true,
    waChatBtnText: initialFooterConfig.waChatBtnText || 'Chat Admin Langsung',
    quickLinks: initialFooterConfig.quickLinks || [
      { id: 'fl-1', label: 'Beranda Utama', targetType: 'page', pageTarget: 'home' },
      { id: 'fl-2', label: 'Layanan & Paket Produk', targetType: 'page', pageTarget: 'products' },
      { id: 'fl-3', label: 'Portofolio', targetType: 'page', pageTarget: 'portfolio' },
      { id: 'fl-4', label: 'Artikel, Tips & Berita', targetType: 'page', pageTarget: 'blog' },
      { id: 'fl-5', label: 'Galeri Foto & Video', targetType: 'page', pageTarget: 'gallery' },
      { id: 'fl-6', label: 'Tentang Kami', targetType: 'page', pageTarget: 'about' },
      { id: 'fl-7', label: 'Hubungi Kami & Reservasi', targetType: 'page', pageTarget: 'contact' },
    ],
  });

  const [socialData, setSocialData] = useState<SocialLinksConfig>({
    instagram: initialSocialConfig.instagram || settings?.contact?.instagram || '',
    youtube: initialSocialConfig.youtube || settings?.contact?.youtube || '',
    facebook: initialSocialConfig.facebook || settings?.contact?.facebook || '',
    whatsapp: initialSocialConfig.whatsapp || settings?.contact?.whatsappNumber || '',
    tiktok: initialSocialConfig.tiktok || settings?.contact?.tiktok || '',
    linkedin: initialSocialConfig.linkedin || settings?.contact?.linkedin || '',
    twitter: initialSocialConfig.twitter || settings?.contact?.twitter || '',
  });

  const [contactData, setContactData] = useState({
    address:
      settings?.contact?.address ||
      'Jl. Raya Tangkuban Parahu No. 88, Lembang, Bandung Barat, Jawa Barat 40391',
    whatsappNumber: settings?.contact?.whatsappNumber || '6281234567890',
    email: settings?.contact?.email || 'info@bellva.id',
    phone: settings?.contact?.phone || '+62 812-3456-7890',
  });

  const [isSaving, setIsSaving] = useState(false);

  // Link Items Management
  const addQuickLink = () => {
    const newLink: FooterLinkItem = {
      id: `fl-${Date.now()}`,
      label: 'Menu Baru',
      targetType: 'page',
      pageTarget: 'about',
    };
    setFooterData((prev) => ({
      ...prev,
      quickLinks: [...(prev.quickLinks || []), newLink],
    }));
  };

  const removeQuickLink = (id: string) => {
    setFooterData((prev) => ({
      ...prev,
      quickLinks: (prev.quickLinks || []).filter((item) => item.id !== id),
    }));
  };

  const updateQuickLink = (id: string, field: keyof FooterLinkItem, value: any) => {
    setFooterData((prev) => ({
      ...prev,
      quickLinks: (prev.quickLinks || []).map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleSaveFooter = async () => {
    setIsSaving(true);
    try {
      const updatedSocialLinks: SocialLinksConfig = {
        instagram: socialData.instagram?.trim() || '',
        youtube: socialData.youtube?.trim() || '',
        facebook: socialData.facebook?.trim() || '',
        whatsapp: socialData.whatsapp?.trim() || '',
        tiktok: socialData.tiktok?.trim() || '',
        linkedin: socialData.linkedin?.trim() || '',
        twitter: socialData.twitter?.trim() || '',
      };

      const payloadFooter: FooterConfig = {
        ...footerData,
        socialLinks: updatedSocialLinks,
        backgroundColor: footerData.bgColor,
        showSocialLinks: footerData.showSocial,
        showContactInfo: footerData.showContact,
      };

      const targetWa = (contactData.whatsappNumber || socialData.whatsapp || settings?.contact?.whatsappNumber || '').trim();
      const targetPhone = (contactData.phone || settings?.contact?.phone || '').trim();

      const updatedContact = {
        ...settings?.contact,
        address: contactData.address.trim(),
        whatsappNumber: targetWa,
        email: contactData.email.trim(),
        phone: targetPhone,
        instagram: updatedSocialLinks.instagram || '',
        youtube: updatedSocialLinks.youtube || '',
        facebook: updatedSocialLinks.facebook || '',
        tiktok: updatedSocialLinks.tiktok || '',
        linkedin: updatedSocialLinks.linkedin || '',
        twitter: updatedSocialLinks.twitter || '',
      };

      const updatedContactWidget = {
        enabled: settings?.contactWidget?.enabled ?? true,
        ...(settings?.contactWidget || {}),
        whatsappNumber: targetWa,
        phoneNumber: targetPhone,
      };

      const ok = await updateSettingsOnServer({
        footerConfig: payloadFooter,
        contact: updatedContact,
        contactWidget: updatedContactWidget,
      });

      if (ok) {
        showToast(
          'Pengaturan Footer & Media Sosial berhasil disimpan dan terintegrasi ke website!',
          'success',
          'Footer Diperbarui'
        );
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menyimpan pengaturan footer.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div id="footer-manager-container" className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-bold mb-2">
            <Layout className="w-3.5 h-3.5 text-emerald-400" />
            <span>Pusat Kelola Footer Website & Tautan Bawah</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
            Pengaturan Struktur & Konten Footer
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Atur identitas brand, tautan navigasi, medsos resmi, alamat kantor, warna tema, dan teks hak cipta secara presisi.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate('home')}
            className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer border border-slate-200"
            title="Lihat hasil footer di website"
          >
            <ExternalLink className="w-4 h-4 text-slate-600" />
            <span>Lihat di Website</span>
          </button>

          <button
            onClick={handleSaveFooter}
            disabled={isSaving}
            className="px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-md flex items-center justify-center gap-2 transition-all cursor-pointer"
          >
            <Check className="w-4 h-4" />
            <span>{isSaving ? 'Menyimpan...' : 'Simpan Perubahan Footer'}</span>
          </button>
        </div>
      </div>

      {/* Structured Category Tabs */}
      <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-xs flex flex-wrap gap-1">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'all'
              ? 'bg-slate-900 text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Sliders className="w-3.5 h-3.5" />
          <span>Semua Pengaturan</span>
        </button>

        <button
          onClick={() => setActiveTab('identity')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'identity'
              ? 'bg-emerald-700 text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Building className="w-3.5 h-3.5" />
          <span>Kolom 1: Identitas Brand</span>
        </button>

        <button
          onClick={() => setActiveTab('social')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'social'
              ? 'bg-rose-600 text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Share2 className="w-3.5 h-3.5" />
          <span>Media Sosial & Simbol</span>
        </button>

        <button
          onClick={() => setActiveTab('nav')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'nav'
              ? 'bg-blue-600 text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Link2 className="w-3.5 h-3.5" />
          <span>Kolom 2: Navigasi Utama</span>
        </button>

        <button
          onClick={() => setActiveTab('seo')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'seo'
              ? 'bg-amber-600 text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Globe className="w-3.5 h-3.5" />
          <span>Kolom 3: Kategori & SEO</span>
        </button>

        <button
          onClick={() => setActiveTab('contact')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'contact'
              ? 'bg-teal-600 text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Phone className="w-3.5 h-3.5" />
          <span>Kolom 4: Kantor & Kontak</span>
        </button>

        <button
          onClick={() => setActiveTab('bottom')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'bottom'
              ? 'bg-purple-600 text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Shield className="w-3.5 h-3.5" />
          <span>Bilah Copyright & Disclaimer</span>
        </button>

        <button
          onClick={() => setActiveTab('theme')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'theme'
              ? 'bg-indigo-600 text-white shadow-xs'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <Palette className="w-3.5 h-3.5" />
          <span>Warna & Tema</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Forms Section */}
        <div className="lg:col-span-8 space-y-6">
          {/* GRUP 1: IDENTITAS BRAND (KOLOM 1) */}
          {(activeTab === 'all' || activeTab === 'identity') && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
                    1
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      Kolom 1: Identitas & Deskripsi Profil Brand
                    </h3>
                    <p className="text-xs text-slate-500">
                      Tampil pada bagian paling kiri di footer website
                    </p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-200">
                  Header Footer Left
                </span>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">
                  Judul Brand / Nama Perusahaan di Footer <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={footerData.aboutTitle || ''}
                  onChange={(e) => setFooterData({ ...footerData, aboutTitle: e.target.value })}
                  placeholder="misal: Bellva EO Gathering Outbound MICE"
                  className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 font-bold text-slate-900"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Nama brand yang akan berdampingan dengan logo perusahaan pada footer.
                </p>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Teks Ringkasan Profil / Deskripsi Singkat Footer
                </label>
                <textarea
                  rows={3}
                  value={footerData.aboutText || ''}
                  onChange={(e) => setFooterData({ ...footerData, aboutText: e.target.value })}
                  placeholder="Portal resmi event organizer gathering, outbound adventure, MICE..."
                  className="w-full p-3 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-slate-800 leading-relaxed"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Deskripsi singkat perusahaan yang ditampilkan di bawah nama brand.
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-emerald-600" />
                  <span>Logo website diambil otomatis dari logo utama situs.</span>
                </div>
                {settings?.logoUrl ? (
                  <img
                    src={settings.logoUrl}
                    alt="Logo Preview"
                    className="h-6 w-auto object-contain bg-slate-900 p-1 rounded"
                  />
                ) : (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                    Default Icon
                  </span>
                )}
              </div>
            </div>
          )}

          {/* GRUP 2: MEDIA SOSIAL & SIMBOL UNTUK FOOTER */}
          {(activeTab === 'all' || activeTab === 'social') && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-xs">
                    2
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Share2 className="w-4 h-4 text-rose-600" />
                      <span>Tombol Media Sosial & Simbol Akun Resmi</span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Masukkan URL link akun medsos Anda. Tombol dengan ikon resmi akan muncul di footer.
                    </p>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer bg-rose-50 px-3 py-1.5 rounded-xl border border-rose-200">
                  <input
                    type="checkbox"
                    checked={footerData.showSocial}
                    onChange={(e) => setFooterData({ ...footerData, showSocial: e.target.checked })}
                    className="rounded text-rose-600 focus:ring-rose-500 w-4 h-4"
                  />
                  <span className="text-xs font-bold text-rose-800">Tampilkan Tombol Medsos</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Instagram */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg bg-gradient-to-tr from-amber-500 via-rose-500 to-purple-600 text-white flex items-center justify-center">
                        <Instagram className="w-3.5 h-3.5" />
                      </div>
                      <span>Instagram</span>
                    </label>
                    <span className="text-[10px] font-mono text-slate-400">@username</span>
                  </div>
                  <input
                    type="text"
                    value={socialData.instagram || ''}
                    onChange={(e) => setSocialData({ ...socialData, instagram: e.target.value })}
                    placeholder="https://instagram.com/nama_akun"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                  />
                </div>

                {/* YouTube */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg bg-red-600 text-white flex items-center justify-center">
                        <Youtube className="w-3.5 h-3.5" />
                      </div>
                      <span>YouTube</span>
                    </label>
                    <span className="text-[10px] font-mono text-slate-400">@channel</span>
                  </div>
                  <input
                    type="text"
                    value={socialData.youtube || ''}
                    onChange={(e) => setSocialData({ ...socialData, youtube: e.target.value })}
                    placeholder="https://youtube.com/@nama_channel"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                  />
                </div>

                {/* Facebook */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                        <Facebook className="w-3.5 h-3.5" />
                      </div>
                      <span>Facebook</span>
                    </label>
                    <span className="text-[10px] font-mono text-slate-400">/page</span>
                  </div>
                  <input
                    type="text"
                    value={socialData.facebook || ''}
                    onChange={(e) => setSocialData({ ...socialData, facebook: e.target.value })}
                    placeholder="https://facebook.com/nama_halaman"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                  />
                </div>

                {/* WhatsApp */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                        <MessageCircle className="w-3.5 h-3.5" />
                      </div>
                      <span>WhatsApp Official</span>
                    </label>
                    <span className="text-[10px] font-mono text-slate-400">628xxx</span>
                  </div>
                  <input
                    type="text"
                    value={socialData.whatsapp || ''}
                    onChange={(e) => setSocialData({ ...socialData, whatsapp: e.target.value })}
                    placeholder="6281234567890 atau https://wa.me/6281234567890"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                  />
                </div>

                {/* TikTok */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg bg-slate-900 text-cyan-400 flex items-center justify-center">
                        <TikTokIcon className="w-3.5 h-3.5" />
                      </div>
                      <span>TikTok</span>
                    </label>
                    <span className="text-[10px] font-mono text-slate-400">@username</span>
                  </div>
                  <input
                    type="text"
                    value={socialData.tiktok || ''}
                    onChange={(e) => setSocialData({ ...socialData, tiktok: e.target.value })}
                    placeholder="https://tiktok.com/@nama_akun"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                  />
                </div>

                {/* LinkedIn */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg bg-blue-700 text-white flex items-center justify-center">
                        <Linkedin className="w-3.5 h-3.5" />
                      </div>
                      <span>LinkedIn</span>
                    </label>
                    <span className="text-[10px] font-mono text-slate-400">company/name</span>
                  </div>
                  <input
                    type="text"
                    value={socialData.linkedin || ''}
                    onChange={(e) => setSocialData({ ...socialData, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/company/nama_perusahaan"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                  />
                </div>

                {/* Twitter / X */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-lg bg-slate-800 text-white flex items-center justify-center">
                        <TwitterXIcon className="w-3.5 h-3.5" />
                      </div>
                      <span>Twitter / X</span>
                    </label>
                    <span className="text-[10px] font-mono text-slate-400">@username</span>
                  </div>
                  <input
                    type="text"
                    value={socialData.twitter || ''}
                    onChange={(e) => setSocialData({ ...socialData, twitter: e.target.value })}
                    placeholder="https://x.com/nama_akun"
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                  />
                </div>
              </div>
            </div>
          )}

          {/* GRUP 3: KOLOM 2 - NAVIGASI UTAMA & TAUTAN */}
          {(activeTab === 'all' || activeTab === 'nav') && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs">
                    3
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-blue-600" />
                      <span>Kolom 2: Navigasi Utama & Daftar Tautan Cepat</span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Kelola daftar menu tautan halaman internal atau link luar di footer
                    </p>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer bg-blue-50 px-3 py-1.5 rounded-xl border border-blue-200">
                  <input
                    type="checkbox"
                    checked={footerData.showQuickLinks}
                    onChange={(e) =>
                      setFooterData({ ...footerData, showQuickLinks: e.target.checked })
                    }
                    className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                  <span className="text-xs font-bold text-blue-800">Tampilkan Kolom 2</span>
                </label>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">
                  Judul Header Kolom Navigasi
                </label>
                <input
                  type="text"
                  value={footerData.navTitle || ''}
                  onChange={(e) => setFooterData({ ...footerData, navTitle: e.target.value })}
                  placeholder="Navigasi Utama"
                  className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 font-bold text-slate-900"
                />
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700">Daftar Menu Tautan Footer:</span>
                  <button
                    onClick={addQuickLink}
                    className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Tambah Menu Baru</span>
                  </button>
                </div>

                {footerData.quickLinks?.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="flex-1">
                      <label className="text-[10px] font-bold text-slate-400 block mb-0.5">
                        Label Teks Menu
                      </label>
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) => updateQuickLink(item.id, 'label', e.target.value)}
                        placeholder="Nama Menu"
                        className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                      />
                    </div>

                    <div className="w-full sm:w-36">
                      <label className="text-[10px] font-bold text-slate-400 block mb-0.5">
                        Tipe Target
                      </label>
                      <select
                        value={item.targetType}
                        onChange={(e) =>
                          updateQuickLink(item.id, 'targetType', e.target.value as any)
                        }
                        className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-300 rounded-lg text-slate-800"
                      >
                        <option value="page">Halaman Internal</option>
                        <option value="url">URL Eksternal</option>
                      </select>
                    </div>

                    {item.targetType === 'page' ? (
                      <div className="w-full sm:w-44">
                        <label className="text-[10px] font-bold text-slate-400 block mb-0.5">
                          Pilih Halaman Target
                        </label>
                        <select
                          value={item.pageTarget || 'home'}
                          onChange={(e) =>
                            updateQuickLink(item.id, 'pageTarget', e.target.value as PageRoute)
                          }
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-300 rounded-lg text-slate-800 font-medium"
                        >
                          <option value="home">Beranda (/)</option>
                          <option value="about">Tentang Kami (/about)</option>
                          <option value="products">Layanan / Produk (/produk)</option>
                          <option value="portfolio">Portofolio (/portfolio)</option>
                          <option value="blog">Artikel & Berita (/artikel)</option>
                          <option value="gallery">Galeri Foto (/gallery)</option>
                          <option value="contact">Hubungi Kami (/contact)</option>
                        </select>
                      </div>
                    ) : (
                      <div className="w-full sm:w-44">
                        <label className="text-[10px] font-bold text-slate-400 block mb-0.5">
                          URL Eksternal
                        </label>
                        <input
                          type="text"
                          value={item.customUrl || ''}
                          onChange={(e) => updateQuickLink(item.id, 'customUrl', e.target.value)}
                          placeholder="https://..."
                          className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-300 rounded-lg text-slate-800"
                        />
                      </div>
                    )}

                    <div className="pt-2 sm:pt-4">
                      <button
                        onClick={() => removeQuickLink(item.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                        title="Hapus Tautan Ini"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* GRUP 4: KOLOM 3 - KATEGORI & MESIN SEO */}
          {(activeTab === 'all' || activeTab === 'seo') && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">
                    4
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-amber-600" />
                      <span>Kolom 3: Topik, Kategori & Mesin Indeksasi SEO</span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Menampilkan tag topik populer dan tautan XML Sitemap & RSS Feed
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">
                  Judul Header Kolom 3 (Topik & Kategori)
                </label>
                <input
                  type="text"
                  value={footerData.categoriesTitle || ''}
                  onChange={(e) => setFooterData({ ...footerData, categoriesTitle: e.target.value })}
                  placeholder="Topik & Kategori"
                  className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 font-bold text-slate-900"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 border border-slate-200 cursor-pointer">
                  <div>
                    <span className="text-xs font-bold text-slate-800 block">Daftar Tag Kategori</span>
                    <span className="text-[11px] text-slate-500">Tag kategori artikel & produk</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={footerData.showCategories}
                    onChange={(e) =>
                      setFooterData({ ...footerData, showCategories: e.target.checked })
                    }
                    className="rounded text-amber-600 focus:ring-amber-500 w-4 h-4"
                  />
                </label>

                <label className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 border border-slate-200 cursor-pointer">
                  <div>
                    <span className="text-xs font-bold text-slate-800 block">Tautan Mesin SEO (RSS & Sitemap)</span>
                    <span className="text-[11px] text-slate-500">Tautan sitemap.xml & rss.xml</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={footerData.showSeoLinks}
                    onChange={(e) =>
                      setFooterData({ ...footerData, showSeoLinks: e.target.checked })
                    }
                    className="rounded text-amber-600 focus:ring-amber-500 w-4 h-4"
                  />
                </label>
              </div>
            </div>
          )}

          {/* GRUP 5: KOLOM 4 - KANTOR & KONTAK RESERVASI */}
          {(activeTab === 'all' || activeTab === 'contact') && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-xs">
                    5
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-teal-600" />
                      <span>Kolom 4: Kantor & Kontak Reservasi</span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Informasi alamat fisik kantor, nomor WhatsApp, email, dan tombol chat cepat
                    </p>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer bg-teal-50 px-3 py-1.5 rounded-xl border border-teal-200">
                  <input
                    type="checkbox"
                    checked={footerData.showContact}
                    onChange={(e) => setFooterData({ ...footerData, showContact: e.target.checked })}
                    className="rounded text-teal-600 focus:ring-teal-500 w-4 h-4"
                  />
                  <span className="text-xs font-bold text-teal-800">Tampilkan Kolom 4</span>
                </label>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">
                  Judul Header Kolom 4 (Kantor & Kontak)
                </label>
                <input
                  type="text"
                  value={footerData.contactTitle || ''}
                  onChange={(e) => setFooterData({ ...footerData, contactTitle: e.target.value })}
                  placeholder="Kantor & Reservasi"
                  className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 font-bold text-slate-900"
                />
              </div>

              <div className="space-y-4 pt-1">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Alamat Lengkap Kantor</span>
                  </label>
                  <textarea
                    rows={2}
                    value={contactData.address}
                    onChange={(e) => setContactData({ ...contactData, address: e.target.value })}
                    placeholder="Jl. Raya Tangkuban Parahu No. 88, Lembang..."
                    className="w-full p-3 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 text-slate-800"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 flex items-center gap-1.5">
                      <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Nomor WhatsApp Reservasi</span>
                    </label>
                    <input
                      type="text"
                      value={contactData.whatsappNumber}
                      onChange={(e) => setContactData({ ...contactData, whatsappNumber: e.target.value })}
                      placeholder="6281234567890"
                      className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 font-medium text-slate-800"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Email Resmi Perusahaan</span>
                    </label>
                    <input
                      type="text"
                      value={contactData.email}
                      onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                      placeholder="info@perusahaan.id"
                      className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 font-medium text-slate-800"
                    />
                  </div>
                </div>

                {/* Tombol Chat WA Quick CTA */}
                <div className="p-3.5 bg-teal-50/60 border border-teal-200 rounded-xl grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={footerData.showWaChatBtn}
                      onChange={(e) =>
                        setFooterData({ ...footerData, showWaChatBtn: e.target.checked })
                      }
                      className="rounded text-teal-600 focus:ring-teal-500 w-4 h-4"
                    />
                    <div>
                      <span className="text-xs font-bold text-teal-900 block">Tombol Chat WA Cepat</span>
                      <span className="text-[10px] text-teal-700">Tampilkan tombol CTA hijau di footer</span>
                    </div>
                  </label>

                  <div>
                    <label className="text-[10px] font-bold text-teal-800 block mb-0.5">
                      Teks Label Tombol
                    </label>
                    <input
                      type="text"
                      value={footerData.waChatBtnText || ''}
                      onChange={(e) =>
                        setFooterData({ ...footerData, waChatBtnText: e.target.value })
                      }
                      placeholder="Chat Admin Langsung"
                      className="w-full px-3 py-1.5 text-xs bg-white border border-teal-300 rounded-lg text-slate-800 font-bold"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* GRUP 6: BILAH COPYRIGHT, DISCLAIMER & AKSES ADMIN */}
          {(activeTab === 'all' || activeTab === 'bottom') && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs">
                    6
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-600" />
                      <span>Bilah Bawah Footer (Bottom Bar), Copyright & Disclaimer</span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Atur hak cipta, teks standar operasional, dan tombol login admin di footer
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">
                  Teks Hak Cipta (Copyright Text)
                </label>
                <input
                  type="text"
                  value={footerData.copyrightText || ''}
                  onChange={(e) => setFooterData({ ...footerData, copyrightText: e.target.value })}
                  placeholder="Bellva EO Gathering Outbound MICE. All rights reserved."
                  className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 font-bold text-slate-900"
                />
              </div>

              <label className="flex items-center justify-between p-3.5 rounded-xl bg-purple-50/50 border border-purple-200 cursor-pointer self-end">
                  <div>
                    <span className="text-xs font-bold text-purple-900 block">Tombol Login Admin Footer</span>
                    <span className="text-[10px] text-purple-700">Tautan Akses Admin di bilah bawah</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={footerData.showAdminLink}
                    onChange={(e) =>
                      setFooterData({ ...footerData, showAdminLink: e.target.checked })
                    }
                    className="rounded text-purple-600 focus:ring-purple-500 w-4 h-4"
                  />
                </label>
            </div>
          )}

          {/* GRUP 7: TEMA & WARNA FOOTER */}
          {(activeTab === 'all' || activeTab === 'theme') && (
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
                    7
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                      <Palette className="w-4 h-4 text-indigo-600" />
                      <span>Warna & Tema Latar Belakang Footer</span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Pilih nuansa warna yang sesuai dengan estetika website Anda
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-2">
                  Pilih Preset Warna Latar Footer:
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {BG_COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setFooterData({ ...footerData, bgColor: preset.value })}
                      className={`p-2.5 rounded-xl border text-left flex items-center gap-2 transition-all cursor-pointer ${
                        footerData.bgColor === preset.value
                          ? 'border-indigo-600 ring-2 ring-indigo-500/20 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <span
                        className="w-5 h-5 rounded-full border border-white/20 shrink-0 shadow-xs"
                        style={{ backgroundColor: preset.value }}
                      />
                      <span className="text-[11px] font-bold text-slate-700 truncate">
                        {preset.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Warna Background (HEX Kustom)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={footerData.bgColor || '#020617'}
                      onChange={(e) => setFooterData({ ...footerData, bgColor: e.target.value })}
                      className="w-9 h-9 rounded-lg border border-slate-300 p-0.5 cursor-pointer shrink-0"
                    />
                    <input
                      type="text"
                      value={footerData.bgColor || '#020617'}
                      onChange={(e) => setFooterData({ ...footerData, bgColor: e.target.value })}
                      placeholder="#020617"
                      className="w-full px-3 py-2 text-xs font-mono bg-slate-50 border border-slate-300 rounded-lg text-slate-800 uppercase"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Warna Teks Footer (HEX Kustom)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={footerData.textColor || '#94a3b8'}
                      onChange={(e) => setFooterData({ ...footerData, textColor: e.target.value })}
                      className="w-9 h-9 rounded-lg border border-slate-300 p-0.5 cursor-pointer shrink-0"
                    />
                    <input
                      type="text"
                      value={footerData.textColor || '#94a3b8'}
                      onChange={(e) => setFooterData({ ...footerData, textColor: e.target.value })}
                      placeholder="#94a3b8"
                      className="w-full px-3 py-2 text-xs font-mono bg-slate-50 border border-slate-300 rounded-lg text-slate-800 uppercase"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Warna Teks Deskripsi (HEX Kustom)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={footerData.textColor || '#94a3b8'}
                      onChange={(e) => setFooterData({ ...footerData, textColor: e.target.value })}
                      className="w-9 h-9 rounded-lg border border-slate-300 p-0.5 cursor-pointer shrink-0"
                    />
                    <input
                      type="text"
                      value={footerData.textColor || '#94a3b8'}
                      onChange={(e) => setFooterData({ ...footerData, textColor: e.target.value })}
                      placeholder="#94a3b8"
                      className="w-full px-3 py-2 text-xs font-mono bg-slate-50 border border-slate-300 rounded-lg text-slate-800 uppercase"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Save Bar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
            <span className="text-xs text-slate-500 font-medium">
              Pastikan Anda mengklik simpan untuk memperbarui tampilan footer di website.
            </span>
            <button
              onClick={handleSaveFooter}
              disabled={isSaving}
              className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-md flex items-center gap-2 transition-all cursor-pointer"
            >
              <Check className="w-4 h-4" />
              <span>{isSaving ? 'Menyimpan...' : 'Simpan Semua Perubahan Footer'}</span>
            </button>
          </div>
        </div>

        {/* Right Column: Live Interactive Footer Preview Box */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs sticky top-6 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-bold text-slate-900">
                  Pratinjau Langsung (Live Preview)
                </h3>
              </div>
              <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                Real-Time
              </span>
            </div>

            <p className="text-[11px] text-slate-500">
              Berikut adalah bentuk tampilan footer yang akan tampil di bagian paling bawah website:
            </p>

            {/* Simulated Live Preview Box */}
            <div
              className="rounded-xl p-4 text-white text-[11px] space-y-4 transition-colors shadow-inner border border-slate-800"
              style={{ backgroundColor: footerData.bgColor || '#020617' }}
            >
              {/* Col 1 preview */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {settings?.logoUrl ? (
                    <img
                      src={settings.logoUrl}
                      alt={footerData.aboutTitle || 'Logo'}
                      className="h-6 w-auto max-h-7 max-w-[140px] object-contain"
                    />
                  ) : (
                    <>
                      <div className="w-6 h-6 rounded-lg bg-emerald-600 flex items-center justify-center font-bold text-white text-xs">
                        <Layers className="w-3.5 h-3.5" />
                      </div>
                      <span className="font-bold text-xs text-white truncate">
                        {footerData.aboutTitle || settings?.siteName || 'Greenjobs ITB'}
                      </span>
                    </>
                  )}
                </div>

                <p className="text-[10px] leading-relaxed line-clamp-3" style={{ color: footerData.textColor }}>
                  {footerData.aboutText}
                </p>

                {footerData.showSocial && (
                  <div className="flex items-center gap-1.5 pt-1">
                    {socialData.instagram && (
                      <span className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center text-rose-400">
                        <Instagram className="w-3 h-3" />
                      </span>
                    )}
                    {socialData.youtube && (
                      <span className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center text-red-500">
                        <Youtube className="w-3 h-3" />
                      </span>
                    )}
                    {socialData.facebook && (
                      <span className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center text-blue-500">
                        <Facebook className="w-3 h-3" />
                      </span>
                    )}
                    {socialData.whatsapp && (
                      <span className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center text-emerald-400">
                        <MessageCircle className="w-3 h-3" />
                      </span>
                    )}
                    {socialData.tiktok && (
                      <span className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center text-cyan-400">
                        <TikTokIcon className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Col 2 Quick links preview */}
              {footerData.showQuickLinks && (
                <div className="pt-2 border-t border-slate-800/80">
                  <span className="font-bold text-[10px] text-white uppercase block mb-1">
                    {footerData.navTitle}
                  </span>
                  <div className="grid grid-cols-2 gap-1 text-[10px]" style={{ color: footerData.textColor }}>
                    {footerData.quickLinks?.slice(0, 4).map((l, i) => (
                      <div key={i} className="truncate">• {l.label}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Preview */}
              {footerData.showContact && (
                <div className="pt-2 border-t border-slate-800/80 space-y-1">
                  <span className="font-bold text-[10px] text-white uppercase block">
                    {footerData.contactTitle}
                  </span>
                  <div className="text-[10px] space-y-0.5" style={{ color: footerData.textColor }}>
                    <div className="truncate">📍 {contactData.address}</div>
                    <div>📞 WA: {socialData.whatsapp || contactData.whatsappNumber}</div>
                  </div>
                </div>
              )}

              {/* Copyright preview */}
              <div className="pt-2 border-t border-slate-800 text-[9px] text-slate-400 flex items-center justify-between">
                <span>© {new Date().getFullYear()} {footerData.copyrightText}</span>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={handleSaveFooter}
                disabled={isSaving}
                className="w-full py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-md flex items-center justify-center gap-2 transition-all cursor-pointer"
              >
                <Check className="w-4 h-4" />
                <span>{isSaving ? 'Menyimpan...' : 'Simpan Perubahan Footer'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
